import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";
const BASE_URL = process.env.REACT_APP_BASE_URL;

// all permissions
export const fetchPermissions = createAsyncThunk(
  "permissions/fetchAll",
  async (arg = 1, { rejectWithValue }) => {
    try {
      const page = typeof arg === "object" && arg != null ? arg.page ?? 1 : arg;
      const per_page =
        typeof arg === "object" && arg != null ? arg.per_page ?? 20 : 20;
      const response = await axios.get(
        `${BASE_URL}/permissions?page=${page}&per_page=${per_page}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed load data";
      return rejectWithValue(message);
    }
  }
);


// add permissions
export const addPermission = createAsyncThunk(
  "permissions/add",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/permissions/store`, userData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "An unknown error occurred";
      return rejectWithValue(message);
    }
  }
);
// get permission record
export const permissionRecord = createAsyncThunk(
  "permissions/permissionRecord",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/permissions/show/${id}`);
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "An unknown error occurred";
      return rejectWithValue({ message });
    }
  }
);
// update permissions
export const updatePermision = createAsyncThunk(
  "permissions/updatePermision",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/permissions/update`,
        { permission_id: id, ...formData },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "An unknown error occurred";
      return rejectWithValue(message);
    }
  }
);
// delete permission
export const deletePermission = createAsyncThunk(
  "permissions/deletePermission",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/permissions/delete`,
        { permission_id: id },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to delete permission";
      return rejectWithValue({ message });
    }
  }
);
const PermissionsSlice = createSlice({
  name: "permissions",
  initialState: {
    permissions: [],
    record: null,
    isLoading: false,
    error: null,
    success: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPermissions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.permissions = action.payload.data;
        state.pagination = action.payload.data.pagination;
      })
      .addCase(fetchPermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load data";
      })
      .addCase(addPermission.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addPermission.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(addPermission.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to add data";
      })
      .addCase(permissionRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(permissionRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.record = action.payload.data;
      })
      .addCase(permissionRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to add data";
      })
      .addCase(updatePermision.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePermision.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(updatePermision.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          action.payload ||
          "Failed to update record";
      })
      .addCase(deletePermission.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deletePermission.fulfilled, (state, action) => {
        state.isLoading = false;

        // Remove from data array inside permissions
        if (state.permissions?.data) {
          state.permissions.data = state.permissions.data.filter(
            (record) => record.id !== action.meta.arg
          );
        }

        state.success = action.payload?.message || "Deleted successfully";
      })
      .addCase(deletePermission.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          action.payload ||
          action.error?.message ||
          "Failed to delete permission";
      });
  },
});

export const { clearState } = PermissionsSlice.actions;
export default PermissionsSlice.reducer;
